import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { IEpoch } from "../interfaces/IEpoch";
import { useTimelineMapStore } from "../store/timelineMapStore";
import { ITranslations } from "../interfaces/ITranslations";
import Rellax from "rellax";
import { forEach } from "@splidejs/splide/src/js/utils";
import type { Hostname } from "../types/hostname";
import {IMapboxConfig} from "@features/Pages/TimelineMapPage/_js/app/interfaces/IMapboxConfig";

export const useTimelineMapService = () => {
  const currentVisibleItem = ref<string>('');
  const router = useRouter();
  const route = useRoute()
  const registerScrollListener = () => {
    document.addEventListener('DOMContentLoaded', () => {
      const containerElement = document.getElementById('timeline') as HTMLElement;

      containerElement.addEventListener('inViewport', (e: Event) => {
        e.stopPropagation()
        const event = e as CustomEvent;

        if (currentVisibleItem.value != event.detail.id) {
          currentVisibleItem.value = event.detail.id;
          router.push(event.detail.id);
        }
      });
    });
  };

  const registerParallax = () => {
    document.addEventListener("DOMContentLoaded", function() {
      const viewWidth = document.documentElement.clientWidth
      let parallaxSpeed = 3;

      if (viewWidth > 1260) {
        parallaxSpeed = 6;
      }

      // Add padding to compensate for parallax scrolling
      Array.from(document.getElementsByClassName('rellax')).forEach((element) => {
        const htmlElement = element as HTMLElement;
        const offset = getDistanceToDocumentLeft(htmlElement);

        new Rellax(htmlElement, {
          horizontal: true,
          vertical: false,
          speed: parallaxSpeed,
          wrapper: '#timeline'
        });
      });
    });

    // Wait for next tick to ensure the elements are initialized
    setTimeout(() => {
      // Add scroll listener to the timeline to detect when the elements are in the viewport
      document.querySelector("#timeline")?.addEventListener('scroll', function() {
        (Array.from(document.getElementsByClassName('parallax-wrapper')) as HTMLElement[]).forEach((parallaxWrapper) => {
          const parent = parallaxWrapper.parentElement as HTMLElement;
          const viewWidth = document.documentElement.clientWidth
          const offset = getDistanceToDocumentLeft(parent);

          // If the element is not initialized and is in the viewport, initialize it
          if (!parent.classList.contains('rellax-initialized')
            && parent.getBoundingClientRect().left < document.documentElement.clientWidth
            && parent.getBoundingClientRect().right > 0
            && offset > viewWidth) {
            // Look at the image to get the transform value
            let transformValue = (parallaxWrapper.querySelector('.rellax') as HTMLElement).style.transform;

            // Invert the transform value to move the rellax element to the left
            parallaxWrapper.style.transform = transformValue.replace(/translate3d\(([^,]+),([^,]+),([^,]+)\)/, function(match, p1, p2, p3) {
              return `translate3d(${-parseInt(p1)}px, ${p2}, ${p3})`;
            });

            parent.classList.add('rellax-initialized');
          }
        });
      });
    });
  };

  const scrollToMoment = () => {
    document.addEventListener("DOMContentLoaded", function() {
      if (!!route.params.id) {
        const container = document.getElementById('timeline') as HTMLElement;
        const target = document.getElementById(route.params.id as string) as HTMLElement;
        const child = target.firstElementChild as HTMLElement;
        const offset = getDistanceToDocumentLeft(target);

        // Position element in the center of the screen
        const position = offset - ((window.innerWidth - target.offsetWidth) / 2) + child.offsetLeft / 2;

        // Scroll to the position with a smooth animation
        const speed = 250;
        const distance = Math.abs(position - container.scrollLeft);
        const duration = distance / speed * 100;

        function scrollToTarget(element: HTMLElement, to: number, duration: number) {
          const perTick = 10 * (speed / 100);

          if (duration <= 0) {
            return;
          }

          setTimeout(function() {
            element.scrollLeft += perTick;
            if ((perTick > 0 && element.scrollLeft >= to) || (perTick < 0 && element.scrollLeft <= to)) {
              element.scrollLeft = to;
              return;
            }
            scrollToTarget(element, to, duration - 10);
          }, 10);
        }

        scrollToTarget(container, position, duration);
      }
    });
  }

  const getHostname = async() => {
    const timelineStore = useTimelineMapStore();

    const hostname = document.getElementById('js-hostname') as HTMLInputElement;
    timelineStore.hostname = hostname.value as Hostname;
  }

  const getShowMap = async () => {
    const timelineStore = useTimelineMapStore();

    const showMap = document.getElementById('js-show-map') as HTMLInputElement;
    if (!showMap) return;

    timelineStore.showMap = showMap.value === 'true';
  };

  const getShowMomentsList = async () => {
    const timelineStore = useTimelineMapStore();

    const showMomentsList = document.getElementById('js-show-moments-list') as HTMLInputElement;
    if (!showMomentsList) return;

    timelineStore.showMomentsList = showMomentsList.value === 'true';
  };

  const getTimelineMapData = async() => {
    const timelineStore = useTimelineMapStore();

    const epochData = document.getElementById('js-timeline-map-data') as HTMLInputElement;
    timelineStore.epochs = JSON.parse(epochData.value) as IEpoch[];
  }
  
  const getMapboxConfig = async() => {
    const timelineStore = useTimelineMapStore();

    const mapboxConfig = document.getElementById('js-mapbox-config') as HTMLInputElement;
    timelineStore.mapboxConfig = JSON.parse(mapboxConfig.value) as IMapboxConfig;
  }

  const getTranslations = async() => {
    const timelineStore = useTimelineMapStore();
    const translations = document.getElementById('js-translations') as HTMLInputElement;
    timelineStore.translations = JSON.parse(translations.value) as ITranslations;
  }

  const getModalContent = async() => {
    const store = useTimelineMapStore();
    const currentLanguage = document.getElementById('js-current-language') as HTMLInputElement;

    forEach(store.epochs, (epoch) => {
      forEach(epoch.timelineMapItems, async(item) => {
        if (item.id) {
          try {
            item.isLoading = true;
            const response = await fetch(`/api/timeline/modalcontent/${item.id}/${currentLanguage.value}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'text/html',
                'Accept': 'text/html'
              }
            });

            if (!response.ok) {
              throw new Error(response.statusText);
            }

            item.modalContent = await response.text();
            let scripts = findScriptTag(item.modalContent);
            if (scripts) {
              item.scripts = JSON.parse(scripts.innerHTML);
            } else {
              item.scripts = [];
            }
          } catch (error) {
            item.modalContent = 'Could not fetch content';
          } finally {
            item.isLoading = false;
          }
        }
      });
    });
  };

  const findScriptTag = (htmlString: string) => {
    // Create a new DOMParser instance
    const parser = new DOMParser();

    // Parse the HTML string into a Document
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Find the script tag with id "component-script"
      return doc.querySelector('script#component-script');
  }

  const getDistanceToDocumentLeft = (element: HTMLElement) => {
    let distance = 0;
    while (element.offsetParent) {
      distance += element.offsetLeft;
      element = element.offsetParent as HTMLElement;
    }
    return distance;
  }

  const trimWindowTypes = (windowType?: string): string => {
    if (!windowType) { return ''; }
    
    return windowType.replace('timeline-window--', '');
  };

  return {
    registerScrollListener,
    scrollToMoment,
    getHostname,
    getShowMap,
    getShowMomentsList,
    getTimelineMapData,
    getMapboxConfig,
    getTranslations,
    registerParallax,
    getModalContent,
    getDistanceToDocumentLeft,
    trimWindowTypes
  }
};
